import { useTranslation } from 'react-i18next';
import Text from 'antd/lib/typography/Text';
import React, { useCallback } from 'react';
import numberHelpers from '../../helpers/number';
import ZLButton, { ButtonSize, ButtonType } from '../zl-button/zl-button';
import { IApplicationState } from '../../store';
import { useSelector } from 'react-redux';

export interface IVoucherAmountButtonProps {
    voucherAmount: number;
    currencySymbol: string;
    buttonIndex: number;
    selected: boolean;
    discountPercentage?: number;
    showTicketLabel: boolean;
    onClick?: (voucherAmmount: number, buttonIndex: number) => void;
}

const VoucherAmountButton: React.FunctionComponent<IVoucherAmountButtonProps> = (props) => {
    const { voucherAmount, currencySymbol, buttonIndex, selected, discountPercentage, onClick, showTicketLabel } = props;

    const selectedLanguage = useSelector((state: IApplicationState) => state.intl.selectedLanguage);

    const isCzech = selectedLanguage.code?.startsWith('cs');
    const { t } = useTranslation();

    const discountAmount = voucherAmount * (1 - (discountPercentage ?? 0));
    const buttonDiscountStr = currencySymbol + discountAmount.toFixed(2).toString();

    const buttonAmountStr = isCzech
        ? currencySymbol + voucherAmount.toFixed(0).toString()
        : currencySymbol + voucherAmount.toFixed(2).toString();

    //console.log(`button index ${buttonIndex} has ammount ${voucherAmount} and display ${buttonAmountStr}`);

    let numberOfTicketsString = undefined;
    if (showTicketLabel) {
        //index from zero but count tickets from 1
        const ticketCount = buttonIndex + 1;
        const numberStr = numberHelpers.toNumber(selectedLanguage.code, ticketCount);
        if (isCzech) {
            if (ticketCount === 1) {
                // Singular
                numberOfTicketsString = numberStr + ' ' + t('giftVoucherSection.purchase.singleTicketLabel');
            } else if (ticketCount >= 2 && ticketCount <= 4) {
                // Nominative plural
                numberOfTicketsString = numberStr + ' ' + t('giftVoucherSection.purchase.czechNominativePlural');
            } else {
                // Genitive plural
                numberOfTicketsString = numberStr + ' ' + t('giftVoucherSection.purchase.czechGenitivePlural');
            }
        } else {
            // For non-Czech locales, default to single vs. multiple
            if (ticketCount > 1) {
                numberOfTicketsString = numberStr + ' ' + t('giftVoucherSection.purchase.multipleTicketLabel');
            } else {
                numberOfTicketsString = numberStr + ' ' + t('giftVoucherSection.purchase.singleTicketLabel');
            }
        }
    }

    const buttonClicked = useCallback(() => {
        if (onClick) onClick(voucherAmount, buttonIndex);
    }, [onClick, voucherAmount, buttonIndex]);

    return (
        <ZLButton
            type={ButtonType.Default}
            selected={selected}
            size={ButtonSize.Large}
            className="zl-voucher-amount-button"
            block
            tall
            onClick={buttonClicked}
        >
            <div style={{ whiteSpace: 'normal' }}>
                {showTicketLabel && numberOfTicketsString &&
                    <>
                        <Text>{numberOfTicketsString}</Text>
                        <p></p>
                    </>}
                {discountPercentage && (
                    <>
                        <Text className="zl-discount-amount">
                            {t('giftVoucherSection.purchase.percentOff', { percentage: discountPercentage * 100 })}
                        </Text>
                        <br></br>
                    </>
                )}

                {discountPercentage ? (
                    <Text className="zl-discounted-value">
                        {t('giftVoucherSection.purchase.valueFor', {
                            credit: buttonAmountStr,
                            price: buttonDiscountStr,
                        })}
                    </Text>
                ) : (
                    <Text>
                        {buttonAmountStr}
                    </Text>
                )}
            </div>
        </ZLButton>
    );
};

export default React.memo(VoucherAmountButton);
